// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import updateSearchFields from "@insite/client-framework/Store/Pages/UserList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import UserSetupPageTypeLink from "@insite/content-library/Components/UserSetupPageTypeLink";
import DataTable, { DataTablePresentationProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { tableProps } from "@store/Themes/common/props/table";
import { tableGetEllipsisCellProps } from "@store/Themes/common/props/table";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import { historyTableProps } from "@store/Themes/common/props/history";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    accountsDataView: getAccountsDataView(state, state.pages.userList.getAccountsParameter),
    parameter: state.pages.userList.getAccountsParameter,
    defaultPageSize: getSettingsCollection(state).websiteSettings.defaultPageSize,
    displayEmail: !getSettingsCollection(state).accountSettings.useEmailAsUserName,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserListUsersTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    usernameHeader?: DataTableHeaderPresentationProps;
    nameHeader?: DataTableHeaderPresentationProps;
    emailHeader?: DataTableHeaderPresentationProps;
    statusHeader?: DataTableHeaderPresentationProps;
    roleHeader?: DataTableHeaderPresentationProps;
    lastSignInHeader?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    usernameCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    emailCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    roleCells?: DataTableCellProps;
    lastSignInCells?: DataTableCellProps;
    pagination?: PaginationPresentationProps;
    // Starter Custom Code
    loadingOverlay?: LoadingOverlayProps;
}

export const userListUsersTableStyles: UserListUsersTableStyles = {
    loadingOverlay: tableProps.loadingOverlay,
    container: tableProps.wrapper,
    dataTable: tableProps.dataTable,
    noResultsText: typographyGetNoResultsAltProps({ spaced: true }),
    /* Store Custom Code - Start */
    usernameCells: {
        css: css`
            ${historyTableProps.shipToCell?.css}
            > a {
                position: absolute;
                width: 85%;
                overflow: hidden;
                top: 50%;
                transform: translateY(-50%);
                text-overflow: ellipsis;
                white-space: nowrap;
                display: block;
            }
        `,
    },

    /* Store Custom Code - End */
    nameCells: historyTableProps.shipToCell,
    emailCells: historyTableProps.shipToCell,
};

const styles = userListUsersTableStyles;

const UserListUsersTable = ({
    accountsDataView: baseAccountsDataView, // Starter Custom Code
    parameter,
    defaultPageSize,
    displayEmail,
    updateSearchFields,
}: Props) => {
    const accountsDataView = usePersistentDataView(baseAccountsDataView); // Starter Custom Code
    const handleChangePage = (page: number) => {
        updateSearchFields({ page });
    };
    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };
    const headerClick = (sortField: string) => {
        const sort = parameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: SortOrderOptions | undefined;
        if (parameter.sort === sortField) {
            sorted = SortOrderOptions.ascending;
        } else if (parameter.sort === `${sortField} DESC`) {
            sorted = SortOrderOptions.descending;
        }
        return sorted;
    };

    // Starter Removed - accountsDataView.isLoading

    // Starter Removed - return null

    return (
        <DataViewLoadingOverlay dataView={accountsDataView} {...styles.loadingOverlay} /* Starter Custom Code */>
            {!accountsDataView.value?.length /* Starter Custom Code */ ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="userListUsersTable_noUsersFound">
                        {translate("No users found")}
                    </Typography>
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...styles.container} data-test-selector="userListUsersTable">
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader
                                    {...styles.usernameHeader}
                                    sorted={sorted("UserName")}
                                    onSortClick={() => headerClick("UserName")}
                                    data-test-selector="userListUsersTable_usernameHeader"
                                >
                                    {translate("Username")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.nameHeader}
                                    sorted={sorted("LastName")}
                                    onSortClick={() => headerClick("LastName")}
                                    data-test-selector="userListUsersTable_nameHeader"
                                >
                                    {translate("Name")}
                                </DataTableHeader>
                                {displayEmail && (
                                    <DataTableHeader
                                        {...styles.emailHeader}
                                        data-test-selector="userListUsersTable_emailHeader"
                                    >
                                        {translate("Email")}
                                    </DataTableHeader>
                                )}
                                <DataTableHeader
                                    tight
                                    {...styles.statusHeader}
                                    data-test-selector="userListUsersTable_statusHeader"
                                >
                                    {translate("Status")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.roleHeader}
                                    tight
                                    sorted={sorted("Role")}
                                    onSortClick={() => headerClick("Role")}
                                    data-test-selector="userListUsersTable_roleHeader"
                                >
                                    {translate("Role")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                    {...styles.lastSignInHeader}
                                    data-test-selector="userListUsersTable_lastSignInHeader"
                                >
                                    {translate("Last Sign In")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {/* Starter Custom - next line - users */}
                                {accountsDataView.value.map(account => (
                                    <DataTableRow
                                        {...styles.dataTableRow}
                                        key={account.id}
                                        data-test-selector="userListUsersTable_row"
                                        data-test-key={account.userName}
                                    >
                                        <DataTableCell
                                            {...styles.usernameCells}
                                            as="th"
                                            scope="row"
                                            data-test-selector="userListUsersTable_cell_username"
                                            data-test-key={account.id}
                                        >
                                            <UserSetupPageTypeLink title={account.userName} userId={account.id} />
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_name"
                                            data-test-key={account.id}
                                        >
                                            {`${account.firstName} ${account.lastName}`}
                                        </DataTableCell>
                                        {displayEmail && (
                                            <DataTableCell
                                                {...styles.emailCells}
                                                data-test-selector="userListUsersTable_cell_email"
                                                data-test-key={account.id}
                                            >
                                                {account.email}
                                            </DataTableCell>
                                        )}
                                        <DataTableCell
                                            {...styles.statusCells}
                                            data-test-selector="userListUsersTable_cell_status"
                                            data-test-key={account.id}
                                        >
                                            {account.isApproved &&
                                                account.activationStatus === "EmailNotSent" &&
                                                translate("Email not sent")}
                                            {account.isApproved &&
                                                account.activationStatus === "EmailSent" &&
                                                translate("Email sent")}
                                            {account.isApproved &&
                                                account.activationStatus === "Activated" &&
                                                translate("Active")}
                                            {!account.isApproved && translate("Deactivated")}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.roleCells}
                                            data-test-selector="userListUsersTable_cell_role"
                                            data-test-key={account.id}
                                        >
                                            {account.roleTranslated || account.role}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.lastSignInCells}
                                            data-test-selector="userListUsersTable_cell_lastSignIn"
                                            data-test-key={account.id}
                                        >
                                            {account.lastLoginOn && (
                                                <LocalizedDateTime dateTime={account.lastLoginOn} />
                                            )}
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    {accountsDataView.pagination && accountsDataView.pagination?.totalItemCount > 1 && (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={accountsDataView.pagination.totalItemCount}
                            currentPage={accountsDataView.pagination.page}
                            resultsPerPage={accountsDataView.pagination.pageSize}
                            resultsPerPageOptions={accountsDataView.pagination.pageSizeOptions}
                            onChangePage={handleChangePage}
                            onChangeResultsPerPage={handleChangeResultsPerPage}
                            data-test-selector="userListUsersTable_pagination"
                            prevPage={accountsDataView.pagination.prevPageUri}
                            nextPage={accountsDataView.pagination.nextPageUri}
                        />
                    )}
                </>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserListUsersTable),
    definition: {
        allowedContexts: ["UserListPage"],
        group: "User List",
    },
};

export default widgetModule;
